/**
 * Iter 69.2 — Contract Templates editor (Owner-only).
 *
 * Two views in one file:
 *   • List view: 3 template cards with last-updated / version / Edit
 *   • Editor: section-by-section editor with sticky action bar + variables sidebar
 *
 * Section body types: paragraph (textarea), bullets (list of textareas),
 * checklist (list of single-line inputs — Media Partnership scope-of-work).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { contractTemplatesAPI } from '../lib/api';
import {
  FileText, ArrowLeft, Plus, Trash2, GripVertical, ChevronDown, ChevronRight,
  Eye, Save, RotateCcw, Send, AlertTriangle, Copy as CopyIcon, Loader2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { formatDateLong } from '../lib/dateFormat';

const TEMPLATE_DESCRIPTIONS = {
  media_partnership: 'For external clients — scope of work, pricing, deliverables.',
  offer_letter: 'For new hires — position, compensation, probation, responsibilities.',
  employment_agreement: 'Full employment contract — duties, leave, notice, jurisdiction.',
};

function newSectionId() {
  return 'sec_' + Math.random().toString(36).slice(2, 10);
}

function emptySection() {
  return { id: newSectionId(), title: 'NEW SECTION', body_type: 'paragraph', body: '' };
}

// ────────────────────────────────────────────────────────────────────────
// Templates list view
// ────────────────────────────────────────────────────────────────────────
function TemplatesListView({ templates, loading, onPick, onBack }) {
  return (
    <div className="max-w-5xl mx-auto p-8" data-testid="templates-list-view">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 -ml-3" data-testid="templates-back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Contracts
      </Button>
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Contract Templates</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Edit clause text and structure. Changes apply to future contracts only — existing sent contracts keep their original wording.
        </p>
      </div>
      {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin"/>Loading…</div>}
      {!loading && (
        <>
          <div className="mb-5 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/5 text-amber-300 text-[12px]">
            <AlertTriangle className="h-3.5 w-3.5 inline -mt-0.5 mr-2" />
            These templates are starting drafts. Have them reviewed by an employment lawyer in your jurisdiction before using in real terminations or other binding documents.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {templates.map(t => (
            <Card key={t.template_id} className="p-5 hover:border-amber-500/40 transition cursor-pointer"
                  onClick={() => onPick(t.template_id)}
                  data-testid={`tpl-card-${t.template_id}`}>
              <div className="flex items-start justify-between mb-3">
                <FileText className="h-5 w-5 text-amber-500/80" />
                <Badge variant="outline" className="text-[10px]">v{t.version}</Badge>
              </div>
              <h3 className="font-medium mb-1">{t.title}</h3>
              <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{TEMPLATE_DESCRIPTIONS[t.template_id]}</p>
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>Last updated {formatDateLong(t.updated_at)}</span>
                {t.draft && <span className="text-amber-500">Draft</span>}
              </div>
              <Button variant="outline" size="sm" className="mt-4 w-full" data-testid={`tpl-edit-${t.template_id}`}>
                Edit Template
              </Button>
            </Card>
          ))}
          </div>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Section card (paragraph / bullets / checklist)
// ────────────────────────────────────────────────────────────────────────
function SectionCard({ section, idx, onChange, onDelete, onMoveUp, onMoveDown }) {
  const [collapsed, setCollapsed] = useState(false);
  const update = (patch) => onChange({ ...section, ...patch });

  const renderBody = () => {
    if (section.body_type === 'paragraph') {
      return (
        <Textarea value={section.body || ''} rows={6}
                  onChange={e => update({ body: e.target.value })}
                  data-testid={`section-body-${section.id}`}
                  className="font-mono text-xs" />
      );
    }
    if (section.body_type === 'bullets') {
      const items = Array.isArray(section.body) ? section.body : [];
      return (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-muted-foreground mt-2">•</span>
              <Textarea value={it} rows={2}
                        onChange={e => {
                          const next = [...items]; next[i] = e.target.value;
                          update({ body: next });
                        }}
                        className="font-mono text-xs flex-1"
                        data-testid={`section-bullet-${section.id}-${i}`} />
              <Button variant="ghost" size="sm" onClick={() => {
                const next = items.filter((_, j) => j !== i);
                update({ body: next });
              }} data-testid={`section-bullet-del-${section.id}-${i}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => update({ body: [...items, ''] })}
                  data-testid={`section-bullet-add-${section.id}`}>
            <Plus className="h-3 w-3 mr-1" /> Add bullet
          </Button>
        </div>
      );
    }
    // checklist
    const items = Array.isArray(section.body) ? section.body : [];
    return (
      <div className="space-y-2">
        <p className="text-[11px] text-muted-foreground">
          Each row is one checklist item label. The contract form's checkboxes use the published list.
        </p>
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs w-6">{i + 1}.</span>
            <Input value={it}
                   onChange={e => {
                     const next = [...items]; next[i] = e.target.value;
                     update({ body: next });
                   }}
                   className="font-mono text-xs flex-1"
                   data-testid={`section-checklist-${section.id}-${i}`} />
            <Button variant="ghost" size="sm" onClick={() => {
              const next = items.filter((_, j) => j !== i);
              update({ body: next });
            }} data-testid={`section-checklist-del-${section.id}-${i}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => update({ body: [...items, ''] })}
                data-testid={`section-checklist-add-${section.id}`}>
          <Plus className="h-3 w-3 mr-1" /> Add item
        </Button>
      </div>
    );
  };

  return (
    <Card className="p-4" data-testid={`section-card-${section.id}`}>
      <div className="flex items-start gap-2 mb-3">
        <Button variant="ghost" size="sm" className="cursor-grab opacity-50" disabled>
          <GripVertical className="h-4 w-4" />
        </Button>
        <span className="text-xs text-muted-foreground mt-2 w-6 text-right">{idx + 1}.</span>
        <Input value={section.title}
               onChange={e => update({ title: e.target.value })}
               className="font-medium flex-1"
               data-testid={`section-title-${section.id}`} />
        <select value={section.body_type}
                onChange={e => {
                  const newType = e.target.value;
                  let newBody = section.body;
                  if (newType === 'paragraph' && Array.isArray(section.body)) {
                    newBody = section.body.join('\n');
                  } else if ((newType === 'bullets' || newType === 'checklist') && typeof section.body === 'string') {
                    newBody = section.body.split('\n').filter(x => x.trim());
                  }
                  update({ body_type: newType, body: newBody });
                }}
                className="bg-background border border-input rounded px-2 py-1 text-xs"
                data-testid={`section-type-${section.id}`}>
          <option value="paragraph">Paragraph</option>
          <option value="bullets">Bullets</option>
          <option value="checklist">Checklist</option>
        </select>
        <Button variant="ghost" size="sm" onClick={() => onMoveUp(idx)} disabled={idx === 0}
                data-testid={`section-up-${section.id}`}>↑</Button>
        <Button variant="ghost" size="sm" onClick={() => onMoveDown(idx)}
                data-testid={`section-down-${section.id}`}>↓</Button>
        <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)}
                data-testid={`section-toggle-${section.id}`}>
          {collapsed ? <ChevronRight className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(idx)}
                className="text-red-500/70 hover:text-red-500"
                data-testid={`section-delete-${section.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {!collapsed && (
        <div className="pl-12 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">
              Lead-in line (optional)
            </label>
            <Input value={section.lead_in || ''}
                   onChange={e => update({ lead_in: e.target.value })}
                   placeholder="Optional intro line shown above the body"
                   className="font-mono text-xs"
                   data-testid={`section-leadin-${section.id}`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">
              Body
            </label>
            {renderBody()}
          </div>
        </div>
      )}
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Editor view
// ────────────────────────────────────────────────────────────────────────
function TemplateEditor({ templateId, onBack }) {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await contractTemplatesAPI.get(templateId);
      setDoc(r.data);
      setDraft(r.data.draft || r.data.published);
    } catch (e) {
      toast.error('Failed to load template');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = useMemo(() => {
    if (!doc || !draft) return false;
    return JSON.stringify(draft) !== JSON.stringify(doc.published);
  }, [doc, draft]);

  const updateDraft = (patch) => setDraft(prev => ({ ...prev, ...patch }));
  const updateSection = (idx, next) => {
    const sections = [...draft.sections]; sections[idx] = next;
    updateDraft({ sections });
  };
  const deleteSection = (idx) => {
    if (!window.confirm('Delete this section? This cannot be undone after publish.')) return;
    const sections = draft.sections.filter((_, i) => i !== idx);
    updateDraft({ sections });
  };
  const addSection = () => updateDraft({ sections: [...draft.sections, emptySection()] });
  const moveSection = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= draft.sections.length) return;
    const sections = [...draft.sections];
    [sections[idx], sections[j]] = [sections[j], sections[idx]];
    updateDraft({ sections });
  };

  const handleSaveDraft = async () => {
    setBusy(true);
    try {
      const r = await contractTemplatesAPI.saveDraft(templateId, draft);
      const unknown = r.data?.unknown_variables || [];
      if (unknown.length > 0) {
        toast.warning(`Saved. Unknown variables found: ${unknown.map(u => '{'+u+'}').join(', ')} — these render as literal text.`);
      } else {
        toast.success('Draft saved');
      }
      await load();
    } catch (e) {
      toast.error('Save failed');
    } finally { setBusy(false); }
  };

  const handlePublish = async () => {
    setBusy(true);
    try {
      // Always save draft first
      await contractTemplatesAPI.saveDraft(templateId, draft);
      const r = await contractTemplatesAPI.publish(templateId);
      toast.success(`Published v${r.data.version} — applies to new contracts going forward.`);
      setShowPublishConfirm(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally { setBusy(false); }
  };

  const handleReset = async () => {
    setBusy(true);
    try {
      const r = await contractTemplatesAPI.reset(templateId);
      toast.success(`Reset to default. New version: v${r.data.version}`);
      setShowResetConfirm(false);
      await load();
    } catch (e) {
      toast.error('Reset failed');
    } finally { setBusy(false); }
  };

  const handlePreview = async () => {
    setBusy(true);
    try {
      const r = await contractTemplatesAPI.preview(templateId, draft);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Preview failed');
    } finally { setBusy(false); }
  };

  const handleDiscardDraft = () => {
    if (!window.confirm('Discard all unsaved changes?')) return;
    setDraft(doc.published);
  };

  if (loading || !doc) return <div className="p-8 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin"/>Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="template-editor">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-3" data-testid="editor-back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <div className="mt-2 mb-6 flex items-baseline gap-3">
        <h1 className="text-2xl font-semibold">{doc.title}</h1>
        <Badge variant="outline" className="text-[10px]">v{doc.version}</Badge>
        {isDirty && <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-[10px]">Unsaved changes</Badge>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-6">
        {/* LEFT — editor */}
        <div className="space-y-4 pb-32" data-testid="editor-left-col">
          {/* Intro */}
          <Card className="p-4">
            <h3 className="font-medium mb-2 text-sm">Intro Paragraph</h3>
            <Textarea value={draft.intro_paragraph || ''} rows={5}
                      onChange={e => updateDraft({ intro_paragraph: e.target.value })}
                      className="font-mono text-xs"
                      data-testid="editor-intro" />
          </Card>

          {/* Sections */}
          {(draft.sections || []).map((s, idx) => (
            <SectionCard key={s.id || idx} section={s} idx={idx}
                         onChange={(next) => updateSection(idx, next)}
                         onDelete={deleteSection}
                         onMoveUp={(i) => moveSection(i, -1)}
                         onMoveDown={(i) => moveSection(i, +1)} />
          ))}

          <Button variant="outline" onClick={addSection} className="w-full" data-testid="editor-add-section">
            <Plus className="h-4 w-4 mr-2" /> Add Section
          </Button>

          {/* Signatures */}
          <Card className="p-4">
            <h3 className="font-medium mb-3 text-sm">Signature Block</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Left label</label>
                <Input value={draft.signature_label_left || ''}
                       onChange={e => updateDraft({ signature_label_left: e.target.value })}
                       data-testid="editor-sig-label-left" />
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block mt-2">Left name</label>
                <Input value={draft.signature_name_left || ''}
                       onChange={e => updateDraft({ signature_name_left: e.target.value })}
                       data-testid="editor-sig-name-left" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Right label</label>
                <Input value={draft.signature_label_right || ''}
                       onChange={e => updateDraft({ signature_label_right: e.target.value })}
                       data-testid="editor-sig-label-right" />
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block mt-2">Right name</label>
                <Input value={draft.signature_name_right || ''}
                       onChange={e => updateDraft({ signature_name_right: e.target.value })}
                       data-testid="editor-sig-name-right" />
              </div>
            </div>
          </Card>
        </div>

        {/* RIGHT — Variables sidebar */}
        <div className="space-y-4 lg:sticky lg:top-6 self-start">
          <Card className="p-4" data-testid="editor-variables-sidebar">
            <h3 className="font-medium mb-1 text-sm">Available Variables</h3>
            <p className="text-[11px] text-muted-foreground mb-3">
              Click to copy. Variables in curly braces get replaced with actual values when a contract is generated. Don't delete the braces.
            </p>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {(doc.variables || []).map(v => (
                <div key={v.name}
                     className="flex items-start gap-2 p-2 rounded hover:bg-muted/40 cursor-pointer text-xs"
                     onClick={() => {
                       navigator.clipboard.writeText('{' + v.name + '}');
                       toast.success(`Copied {${v.name}}`);
                     }}
                     data-testid={`var-row-${v.name}`}>
                  <CopyIcon className="h-3 w-3 mt-0.5 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <code className="text-amber-500 text-[11px]">{'{' + v.name + '}'}</code>
                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{v.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Sticky bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur border-t z-10">
        <div className="max-w-7xl mx-auto p-3 flex items-center justify-between gap-3" data-testid="editor-action-bar">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {doc.draft && <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-[10px]">Draft on file</Badge>}
            <span>v{doc.version} · last published {doc.published_at ? formatDateLong(doc.published_at) : '—'}</span>
          </div>
          <div className="flex items-center gap-2">
            {isDirty && (
              <Button variant="ghost" size="sm" onClick={handleDiscardDraft} disabled={busy}
                      data-testid="action-discard">
                Discard
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={busy || !isDirty}
                    data-testid="action-save-draft">
              <Save className="h-3 w-3 mr-1" /> Save Draft
            </Button>
            <Button variant="outline" size="sm" onClick={handlePreview} disabled={busy}
                    data-testid="action-preview">
              <Eye className="h-3 w-3 mr-1" /> Preview PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowResetConfirm(true)} disabled={busy}
                    data-testid="action-reset">
              <RotateCcw className="h-3 w-3 mr-1" /> Reset to Default
            </Button>
            <Button size="sm" onClick={() => setShowPublishConfirm(true)} disabled={busy}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                    data-testid="action-publish">
              <Send className="h-3 w-3 mr-1" /> Publish
            </Button>
          </div>
        </div>
      </div>

      {/* Publish confirmation */}
      <Dialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <DialogContent data-testid="publish-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Publish Template
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will affect all <strong>new</strong> contracts going forward. Existing sent contracts keep their original wording. Continue?
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPublishConfirm(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handlePublish} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-black"
                    data-testid="publish-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : null} Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset confirmation */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-500" /> Reset to Default
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will discard all your customizations and restore the original template content. Continue?
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowResetConfirm(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleReset} disabled={busy} className="bg-red-500 hover:bg-red-600 text-white"
                    data-testid="reset-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : null} Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Main export — routes between list and editor
// ────────────────────────────────────────────────────────────────────────
export default function ContractTemplatesPage() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const isOwner = user?.role === 'super_admin' || user?.role === 'owner';

  useEffect(() => {
    if (!isOwner) return;
    (async () => {
      try {
        const r = await contractTemplatesAPI.list();
        setTemplates(r.data || []);
      } catch (e) {
        toast.error('Failed to load templates');
      } finally { setLoading(false); }
    })();
  }, [isOwner]);

  if (!isOwner) {
    return (
      <div className="max-w-md mx-auto p-12 text-center" data-testid="templates-no-access">
        <h2 className="text-lg font-semibold mb-2">Owner access required</h2>
        <p className="text-sm text-muted-foreground mb-6">Contract templates are managed by workspace Owners only.</p>
        <Button onClick={() => navigate('/tasks/contracts')}>Back to Contracts</Button>
      </div>
    );
  }

  if (templateId) {
    return <TemplateEditor templateId={templateId} onBack={() => navigate('/tasks/contracts/templates')} />;
  }
  return <TemplatesListView templates={templates} loading={loading}
                            onPick={(id) => navigate(`/tasks/contracts/templates/${id}`)}
                            onBack={() => navigate('/tasks/contracts')} />;
}
